import { faStar } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StatusCode } from "api/protocols";
import { GetLoyaltyProgramCustomerTransactions, ILoyaltyProgramTransactionExtended } from "api/rpc/2024-04/customer/loyaltyProgram/loyaltyProgram";
import axios, { CancelToken } from "axios";
import { Badge } from "components/badge/Badge";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import { useAppDispatch } from "hooks/redux";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { showError } from "redux/actions/ui";
import { ILoyaltyCustomer } from "redux/reducers/models/loyalty";
import "../portalLoyaltyProgram.scss";

interface IProps {
    customer: ILoyaltyCustomer;
}

const TABLE_LIMIT = 10;

export default function LoyaltyProgramRecentActivity(props: IProps) {
    const {customer} = props;
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const [offset, setOffset] = useState<number>(0);
    const [transactions, setTransactions] = useState<Array<ILoyaltyProgramTransactionExtended>>(undefined);

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getTransactions(source.token);
        return () => source.cancel();
    }, [offset]);

    async function getTransactions(cancelToken: CancelToken) {
        //loader
        if (transactions !== undefined) {
            setTransactions(undefined);
        }
        const res = await GetLoyaltyProgramCustomerTransactions({loyalty_program_id: customer?.loyalty_program_id, limit: TABLE_LIMIT, offset: offset}, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            setTransactions([]);
            dispatch(showError(t("Error getting recent activity")));
            return;
        }
        setTransactions(res?.data);
    }

    function badgeType(kind: "credit" | "debit", amount: number) {
        if (kind === "credit") {
            return <Badge type="success" size="small"><FontAwesomeIcon icon={faStar} className="mr-2"/> + {amount}</Badge>;
        } else {
            return <Badge type="error" size="small"><FontAwesomeIcon icon={faStar} className="mr-2"/> - {amount}</Badge>;
        }
    }

    function getSalesChannel(channel: string) {
        const salesChannel = channel ? channel?.toLocaleLowerCase() : "--";
        switch(salesChannel) {
            case "pos":
                return t("In Store");
            case "booking_engine":
                return t("Online Booking");
            case "online_store":
                return t("Online");
            default: 
                return "--";
        }
    }

    return (
        <Card title={t("Recent Activity")}>
            <Card.Section table="true">
                <DataTable
                loading={!transactions}
                columns={[{label: t("Date")}, {label: t("Activity")}, {label: t("Points")}, {label: t("Running Balance")}]}
                footer={{
                    tableLimit: TABLE_LIMIT,
                    tableOffset: offset,
                    disableNextOffset: !transactions || !(transactions?.length === TABLE_LIMIT),
                    handleTableOffset: direction =>
                      setOffset(prev => direction === "prev" ? prev - TABLE_LIMIT : prev + TABLE_LIMIT),
                  }}
                >
                {transactions?.map((transaction, index) => {
                    return (
                        <tr key={index}>
                            <td><p className="loyalty-program-activity-primary">{moment(transaction?.created_at, "YYYY-MM-DD HH:mm:ss")?.format("LL")}</p><p>{transaction?.facility_long_name}</p></td>
                            <td>{transaction?.loyalty_program_offers_type === "welcome_bonus" ? <p className="loyalty-program-activity-primary">{t("Welcome Bonus")}</p>
                        : <><p className="loyalty-program-activity-primary">{t("Order")} {transaction?.order_number}</p><p>{getSalesChannel(transaction?.order_sales_channel)}</p></>    
                        }</td>
                            <td><p className="loyalty-program-activity-expiry">{badgeType(transaction?.kind, transaction?.amount)} {transaction?.expiry_date && moment(transaction?.expiry_date, "YYYY-MM-DD HH:mm:ss")?.isAfter(moment()) 
                            ? `${t("Exp")}. ${moment(transaction?.expiry_date, "YYYY-MM-DD HH:mm:ss")?.format("LL")}` : transaction?.expiry_date && moment(transaction?.expiry_date, "YYYY-MM-DD HH:mm:ss")?.isBefore(moment()) ? t("Expired") : null}</p></td>
                            <td>{transaction?.new_balance}</td>
                        </tr>
                    );
                })}
                </DataTable>
            </Card.Section>
        </Card>
    );
}